<script lang="ts">
  import { fade } from 'svelte/transition';
  import { getAppConfig } from '../../../../common/config/app-config';
  import {
    formatNumber,
    getContextedUrl,
    getLangSpecificFieldname,
    getStr,
    globalCurrencyTag,
    isMobile,
    reverseChildNodesForArabicLang,
  } from '../../../../common/util';
  import Icon from '../../components/Icon.svelte';
  import Text from '../../components/Text.svelte';
  // import PictureTag from '../PictureTag.svelte';
  import ExtraBrandProductReviewCounts from './ExtraBrandProductReviewCounts.svelte';
  import ExtraBrandProductReviewSection from './ExtraBrandProductReviewSection.svelte';

  export let tileData: any;
  export let isLargeTile: boolean;

  const isResponsive = isMobile();
  const ratedFor = getStr('extra.pdp.reviewSectionText');

  // $: productImageHeight = isResponsive ? '120px' : isLargeTile ? '333px' : '240px';
  $: hasDiscount = tileData?.priceValueDiscountPercentageFlag;
  $: currentPrice = hasDiscount ? tileData?.priceValueDiscount : tileData?.price;
  $: oldPrice = tileData?.wasPrice;
  $: savedPrice = oldPrice - currentPrice;
  $: hasTasheelPrice = tileData?.priceValueTasheel > 0;
  $: tasheelPrice = (hasTasheelPrice && tileData?.priceValueTasheel) || 0;
  const isRatingVisible = true; //tileData?.rating > 0 && tileData?.numberOfReviews > 0;
  $: productUrl = tileData && getContextedUrl(tileData[getLangSpecificFieldname('url', lang)]);
  $: readMoreUrl = composeReadMoreUrl(productUrl);

  const {
    lang,
    unbxdSearchConfig: {
      tasheelInfo: { tasheelImageUrl, tasheelInstallmentMonths },
    },
    bnplBaseetaPaymentModeEnabled,
  } = getAppConfig();

  function composeReadMoreUrl(pdpUrl: string) {
    if (pdpUrl?.length === 0) {
      return '';
    }
    const isAbsoluteUrl = pdpUrl.startsWith('http');
    const dummyBase = 'https://dummy';
    const url = new URL(pdpUrl, dummyBase);
    url.hash = 'reviews_section';
    if (isAbsoluteUrl) {
      return url.href;
    }
    return url.href.replace(dummyBase, '');
  }
</script>

<a href={productUrl} class="product-grid-tile" class:large-tile={isLargeTile} in:fade>
  <section class="product-image">
    <!-- <PictureTag
      image={tileData?.amplienceProductBaseUrl}
      getAllResolutions={false}
      height={productImageHeight}
      imagePosition={isResponsive || isLargeTile ? 'top' : 'center'}
    /> -->
    <img
      src="{window.ACC.config.extraThemeResourcePath}/images/spacer_image.gif"
      loading="lazy"
      srcset="{tileData?.amplienceProductBaseUrl}fmt=auto&w=90 90w, 
            {tileData?.amplienceProductBaseUrl}fmt=auto&w=180 180w, 
            {tileData?.amplienceProductBaseUrl}fmt=auto&h=214&w=214 214w,
            {tileData?.amplienceProductBaseUrl}fmt=auto&h=428&w=428 428w"
      sizes="(max-width: 899px) 90px, 214px"
      fetchpriority="auto"
      alt="noname"
      style={`object-fit: contain; object-position:${isResponsive || isLargeTile ? 'top' : 'center'};`}
    />
  </section>
  {#if isLargeTile || isResponsive}
    <section class="product-reviews">
      {#if isRatingVisible}
        <ExtraBrandProductReviewCounts
          rating={tileData?.testFreakData.rating}
          totalReviews={tileData?.testFreakData.noOfReviews}
          {ratedFor}
        />
      {/if}
      <ExtraBrandProductReviewSection
        totalReviewsToDisplay={isResponsive ? 2 : 3}
        testFreakData={tileData?.testFreakData.reviews}
        {readMoreUrl}
      />
    </section>
  {/if}
  <section class="product-info">
    <h3 class="title">
      {tileData[getLangSpecificFieldname('name', lang)]}
    </h3>
    {#if !isLargeTile && isRatingVisible && !isResponsive}
      <section class="product-info-reviews">
        <ExtraBrandProductReviewCounts
          rating={tileData?.testFreakData.rating}
          totalReviews={tileData?.testFreakData.noOfReviews}
          {ratedFor}
        />
      </section>
    {/if}
    <section class="price">
      <section class="full-price">
        <div class="current-price" use:reverseChildNodesForArabicLang>
          {@html globalCurrencyTag} <strong>{formatNumber(currentPrice)}</strong>
        </div>
        {#if hasDiscount}
          <div class="old-price">
            <span class="strike-out">{formatNumber(oldPrice)}</span>
            <span class="orange-text">
              <Text key="extra.product.cashback.save" />
              <span use:reverseChildNodesForArabicLang>{@html globalCurrencyTag} {formatNumber(savedPrice)}</span>
            </span>
          </div>
        {/if}
      </section>
      {#if hasTasheelPrice && bnplBaseetaPaymentModeEnabled}
        <section class="divider"></section>
        <section class="tasheel-price">
          <div class="current-price">
            <span use:reverseChildNodesForArabicLang
              >{@html globalCurrencyTag}
              <strong>{formatNumber(tasheelPrice)}</strong></span
            >
            <strong>/<Text key="extra.pdp.monthLable" /></strong>
            <span class="tasheel-icon"
              ><Icon type="url" glyph={tasheelImageUrl} alt="Tasheel logo" mobileSize={16} desktopSize={20} /></span
            >
          </div>
          <div class="interest-info">
            <Text key="extra.tasheel.priceValue.desc" args={['0', tasheelInstallmentMonths]} />
          </div>
        </section>
      {/if}
    </section>
  </section>
</a>

<style lang="scss">
  @import '../../styles/colors.new.scss';

  .product-grid-tile {
    border-radius: 12px;
    border: 1px solid $secondary-gray-02;
    display: grid;
    padding: 34px 16px 28px;
    grid-template-columns: 80px 1fr;
    grid-template-rows: min-content;
    grid-template-areas:
      'image info'
      'reviews reviews';
    align-items: flex-start;
    gap: 16px;
    color: $neutral-dark-01;

    @include desktop-min-width {
      padding: 20px 50px 10px 45px;
      border-radius: 20px;
      gap: 0 26px;
      grid-template-columns: 150px 1fr;
      grid-template-rows: min-content;
      grid-template-areas: 'image info';
    }

    .product-image {
      grid-area: image;
      transform: scale(1.2);
    }
    .product-reviews {
      grid-area: reviews;
      border-top: 1px solid $secondary-gray-02;
      padding-top: 16px;

      @include desktop-min-width {
        border-top: 0;
        padding-top: 0;
      }
    }
    .product-info {
      grid-area: info;

      @include desktop-min-width {
        margin-top: 30px;
      }

      .product-info-reviews {
        margin-top: 6px;
      }

      h3 {
        font-size: 16px;
        font-weight: 700;
        line-height: 18px;
        margin-block: 0;

        @include desktop-min-width {
          font-size: 18px;
          font-weight: 400;
          line-height: 20px;
        }
      }

      .price {
        margin-top: 12px;
        display: inline-flex;
        align-items: flex-start;
        gap: 6px;
        flex-direction: column;

        @include desktop-min-width {
          display: flex;
          margin-top: 24px;
          align-items: center;
          gap: 20px;
          flex-direction: row;
        }

        .current-price {
          font-size: 14px;
          font-weight: 400;
          line-height: 16px;
          color: $primary-black;

          @include desktop-min-width {
            font-size: 18px;
            font-weight: 400;
            line-height: 20px;
          }
        }
        .interest-info {
          font-size: 12px;
          font-weight: 400;
          line-height: 14px;
          color: $neutral-dark-03;
        }
        .old-price {
          .strike-out,
          .orange-text {
            font-size: 12px;
            line-height: 14px;
            display: inline-block;
            width: fit-content;
          }
          .strike-out {
            color: $neutral-dark-03;
            font-weight: 400;
            text-decoration: line-through;
          }
          .orange-text {
            color: $extraTagBg;
            font-weight: 700;
            margin-left: 4px;
          }
        }
        .divider {
          background-color: $secondary-gray-02;
          align-self: stretch;
          height: 1px;

          @include desktop-min-width {
            height: unset;
            width: 1px;
          }
        }
        .strike-out {
          font-size: 12px;
          line-height: 14px;
          font-weight: 400;
          color: $neutral-dark-03;
        }
      }
    }

    &.large-tile {
      padding: 20px 40px 50px 30px;
      grid-template-columns: 274px 1fr;
      grid-template-areas:
        'image reviews'
        'info info';

      .product-image {
        padding: 20px;
        text-align: center;
      }
      .product-reviews {
        display: flex;
        flex-direction: column;
        gap: 24px;
        margin-top: 20px;
      }
      .product-info {
        align-self: flex-start;
        margin-top: 0;

        h3 {
          font-size: 20px;
          line-height: 22px;
        }
        .price {
          margin-top: 20px;
        }
      }
    }
  }
</style>
